import { createRouter, createWebHistory } from 'vue-router'

import { useAppStore } from '@/stores/app.store.ts'
import { api } from '../api/index.ts'

const beforeEnterAuthCheck = async (to: { name?: unknown }) => {
  const appStore = useAppStore()

  if (!appStore.isAuthenticated) {
    try {
      const currentUser = await api.user.current()

      appStore.setUser(currentUser)
      appStore.setAuthenticated(true)
    } catch {
      return { name: 'login' }
    }
  }

  if (appStore.user?.mustChangePassword && to.name !== 'change-password') {
    return { name: 'change-password' }
  }
}

const beforeEnterUnauthCheck = () => {
  const appStore = useAppStore()

  if (!appStore.isAuthenticated) {
    return
  }

  return { name: 'home' }
}

const beforeEnterAdminCheck = () => {
  if (!useAppStore().user?.isAdmin) {
    return { name: 'home' }
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/auth',
      component: () => import('@/layouts/AuthLayout.vue'),
      redirect: { name: 'login' },
      beforeEnter: beforeEnterUnauthCheck,
      children: [
        {
          path: 'login',
          name: 'login',
          component: () => import('@/views/auth/LoginView.vue'),
        },
      ],
    },
    {
      path: '/logout',
      name: 'logout',
      component: () => import('@/views/LogoutView.vue'),
    },
    {
      path: '/',
      name: 'index',
      beforeEnter: beforeEnterAuthCheck,
      redirect: { name: 'home' },
      component: () => import('@/layouts/MainLayout.vue'), // or your actual main layout
      children: [
        {
          path: '/home',
          name: 'home',
          component: () => import('@/views/IndexView.vue'),
          meta: {
            sidebar: 'home',
          },
        },
        {
          path: '/change-password',
          name: 'change-password',
          component: () => import('@/views/auth/ChangePasswordView.vue'),
        },
        {
          path: '/profile',
          name: 'profile',
          component: () => import('@/views/ProfileView.vue'),
          meta: {
            sidebar: 'profile',
          },
        },
        {
          path: '/documents',
          name: 'documents',
          component: () => import('@/views/documents/DocumentOverview.vue'),
          meta: {
            sidebar: 'documents',
          },
        },
        {
          path: '/documents/new',
          name: 'document-create',
          component: () => import('@/views/documents/DocumentEditView.vue'),
          meta: {
            sidebar: 'documents',
          },
        },
        {
          path: '/documents/:id',
          name: 'document-edit',
          component: () => import('@/views/documents/DocumentEditView.vue'),
          meta: {
            sidebar: 'documents',
          },
        },
        {
          path: '/admin',
          name: 'admin',
          beforeEnter: beforeEnterAdminCheck,
          redirect: { name: 'users' },
          component: () => import('@/layouts/AdminLayout.vue'),
          children: [
            {
              path: 'users',
              name: 'users',
              component: () => import('@/views/admin/UserManagementView.vue'),
              meta: {
                sidebar: 'admin',
              },
            },
            {
              path: 'departments',
              name: 'departments',
              component: () => import('@/views/admin/DepartmentView.vue'),
              meta: {
                sidebar: 'admin',
              },
            },
            {
              path: 'system-prompts',
              name: 'system-prompts',
              component: () => import('@/views/admin/SystemPromptView.vue'),
              meta: {
                sidebar: 'admin',
              },
            },
            {
              path: 'ai-requests',
              name: 'ai-request-audits',
              component: () => import('@/views/admin/AIRequestAuditView.vue'),
              meta: {
                sidebar: 'admin',
              },
            },
          ],
        },
        {
          path: '/:pathMatch(.*)*',
          name: 'not-found',
          component: () => import('@/views/NotFoundView.vue'),
        },
      ],
    },
  ],
})

export default router
